import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useRef,
} from 'react';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import qs from 'query-string-for-all';
import useThrottleFn from 'react-use/lib/useThrottleFn';
import useDebounce from 'react-use/lib/useDebounce';
import useEffectOnce from 'react-use/lib/useEffectOnce';
import useClickAway from 'react-use/lib/useClickAway';

import { AppContext } from '@prisjakt/ui-app-context';

import AdContainer from 'src/components/ads';
import { SuggestionsResponse, getSuggestionsFromOldFormat } from './oldFormat';
import { getSuggestionsFromNewFormat } from './newFormat';
import OriginalSuggestionItem from './components/list-items/OriginalSuggestionItem';
import SuggestionItem from './components/list-items/SuggestionItem';
import ProductSuggestionItem from './components/list-items/ProductSuggestionItem';
import StoreSuggestionItem from './components/list-items/StoreSuggestionItem';
import CategorySuggestionItem from './components/list-items/CategorySuggestionItem';
import {
  HistoryItem,
  Title as HistoryTitle,
} from './components/list-items/history-item';
import { SuggestionBox } from './suggestion-box';
import Overlay from 'src/components/overlay';
import { SuggestionItems } from './components/items';

import actions from 'src/areas/search/actions/search';
import { useNewSearchPredictions } from 'src/hooks/experiments';
import { graphql } from 'src/globals/api/requests';
import suggestionsQuery from './query.graphql';
import {
  addRecentSearch,
  getRecentSearches,
  deleteRecentSearch,
  clearRecentSearches,
} from './history-storage';
import { extractFilter } from './filter-helpers';

import {
  trackSearchSuggestionClicked,
  trackSearchSuggestionsListed,
  trackSearchHistoryListed,
} from './tracking';

import * as S from './styles';

import { SearchResult, SearchResultError } from '@prisjakt/search-types';

import {
  Suggestion,
  NewSuggestion,
  ValidSuggestionTypes,
  NewSuggestionType,
  HistoryItem as SuggestedHistory,
  OriginalItem,
  NewSuggestionTypeEnum,
  ThinProduct,
  SUGGESTION_ORDER,
} from './types';
import { SuggestedProductCollection, PopularSearch } from 'src/types';
import { PopularSearches } from 'src/components/popular-searches';
import popularSearchesQuery from './requests/popular-searches.graphql';

const NAV_ID_ORIGINAL = 0;

const validTypes: (
  | NewSuggestionType
  | 'history'
  | 'original'
)[] = Object.values(NewSuggestionTypeEnum);

const adsViewUpdate = {
  type: 'ADS_VIEW_UPDATE',
  view: `search_suggestions`,
};

type Props = {
  onClose: () => void;
  includePriceOnProductSuggestions: boolean;
  useNewResponseFormat: boolean;
  query: string;
  setQuery: (query: string) => void;
  visible: boolean;
  updatedInputElement: React.RefObject<HTMLInputElement> | null;
  updatedSearchWrapperElement: React.RefObject<HTMLDivElement> | null;
};

export const gotoUrl = async (
  type: Suggestion['type'],
  id: Suggestion['id'],
  queryParam: string,
  meta?: string
) => {
  const params = { q: queryParam, meta };
  const res = await fetch(
    `/_internal/goto/${type}/${id}?${qs.stringify(params)}`
  );

  if (res.status !== 200) {
    throw new Error(`Cannot resolve url for ${type}`);
  }

  return res.text();
};

const createHistorySuggestion: (
  query: string,
  index: number
) => SuggestedHistory = (query, index) => ({
  type: 'history',
  __typename: 'history',
  text: query,
  navId: 100 + index,
});

const createOriginalSuggestion: (q: string) => OriginalItem = (
  query: string
) => ({
  navId: NAV_ID_ORIGINAL,
  text: query,
  type: 'original',
  __typename: 'original',
});

const mapNewTypes = {
  SuggestedProduct: 'product' as ValidSuggestionTypes,
  SuggestedBrand: 'brand' as ValidSuggestionTypes,
  SuggestedProductCollection: 'product_collection' as ValidSuggestionTypes,
  SuggestedCategory: 'category' as ValidSuggestionTypes,
  SuggestedShop: 'store' as ValidSuggestionTypes,
  SuggestedVirtualCategory: 'vcategory' as ValidSuggestionTypes,
};

const filterHistory = (history: string, query: string) =>
  history.toLowerCase().indexOf(query?.toLowerCase()) > -1;

const Suggestions = (props: Props) => {
  const {
    query,
    onClose,
    includePriceOnProductSuggestions,
    useNewResponseFormat,
    setQuery,
    visible,
    updatedInputElement,
    updatedSearchWrapperElement,
  } = props;

  const { market, t } = useContext(AppContext);
  const [selected, setSelected] = useState(0);
  const [suggestions, setSuggestions] = useState<ReadonlyArray<Suggestion>>([
    ...getRecentSearches().map(createHistorySuggestion),
  ]);
  const [searchInputElement, setSearchInputElement] = useState<
    HTMLInputElement
  >();
  const [searchWrapperElement, setSearchWrapperElement] = useState<
    HTMLDivElement
  >();
  const containerRef = useRef<HTMLUListElement | null>(null);
  const history = useHistory();
  const dispatch = useDispatch();
  // Keep track of the request count to discard old results
  const requestCount = useRef(0);

  const newPredictionsExperiment = useNewSearchPredictions();
  const [newPredictionsApi, setNewPredictions] = useState(
    newPredictionsExperiment
  );
  const [newSuggestions, setNewSuggestions] = useState<NewSuggestion[]>([]);
  const [products, setProducts] = useState<{ id: number; price: number }[]>([]);
  const [popularSearches, setPopularSearches] = useState<PopularSearch[]>([]);

  // useThrottleFn requires the fn to accept all parameters in args
  useThrottleFn(
    (_1, _2) => {
      if (!newPredictionsApi) {
        dispatch(adsViewUpdate);
      }
    },
    1500,
    [suggestions, selected]
  );

  // Tracking
  useDebounce(
    () => {
      const suggestionsCount = newSuggestions.filter(suggestion =>
        validTypes.includes(suggestion.type)
      ).length;
      const historyCount = newSuggestions.filter(
        suggestion => suggestion.type === 'history'
      ).length;

      if (visible && suggestionsCount)
        trackSearchSuggestionsListed(
          newSuggestions.map(item => item.type).join(', ')
        );
      if (visible && historyCount) trackSearchHistoryListed();
    },
    500,
    [newSuggestions, visible]
  );

  useEffectOnce(() => {
    const searchInput = Array.from(
      document.querySelectorAll('[data-suggestions="search"]')
    ).find(
      (el): el is HTMLInputElement =>
        el instanceof HTMLInputElement && el.offsetParent !== null
    );

    const searchWrapper = Array.from(
      document.querySelectorAll('[data-suggestions="searchWrapper"]')
    ).find(
      (el): el is HTMLDivElement =>
        el instanceof HTMLDivElement && el.offsetParent !== null
    );

    if (searchInput) setSearchInputElement(searchInput);

    if (searchWrapper) setSearchWrapperElement(searchWrapper);
  });

  useEffect(() => {
    const getPopularSearches = async () => {
      const response = await graphql(popularSearchesQuery);
      setPopularSearches(response ? response.data?.popularSearches : []);
    };
    getPopularSearches();
  }, []);

  const setCurrentSuggestion = useCallback(
    (suggestion: Suggestion | NewSuggestion) => {
      const modifiedQuery = `${suggestion.text.trim()} `;
      setQuery(modifiedQuery);

      if (!searchInputElement) return;
      searchInputElement.value = modifiedQuery;
      searchInputElement.dispatchEvent(new CustomEvent('change'));
    },
    [searchInputElement, setQuery]
  );

  const handleClickNew = useCallback(
    async (suggestion: NewSuggestion, index: number) => {
      const { push } = history;

      // Tracking
      if (validTypes.includes(suggestion.type)) {
        // Reciprocal rank, aka. the postion of the suggestion divied by 1 to calculate the reciprocal rank.
        const reciprocalRank = 1 / index;
        trackSearchSuggestionClicked(
          suggestion.type,
          suggestion.text,
          query,
          reciprocalRank
        );
      }

      switch (suggestion.__typename) {
        case 'SuggestedVirtualCategory':
        case 'SuggestedCategory':
        case 'SuggestedShop':
        case 'SuggestedProduct': {
          try {
            const url = await gotoUrl(
              mapNewTypes[suggestion.__typename],
              suggestion.id.toString(),
              suggestion.text
            );
            onClose();
            push(url);
            break;
            // eslint-disable-next-line no-empty
          } catch (e) {}
          break;
        }
        case 'SuggestedSearchPhrase':
        case 'SuggestedBrand':
          onClose();
          // Perform a search for the brand name as we don't want to
          // send users to the brand page.
          push(`/search?search=${encodeURIComponent(suggestion.text)}`);
          break;
        case 'SuggestedProductCollection':
          const { path } = extractFilter(suggestion); // eslint-disable-line no-case-declarations

          if (!path && !suggestion?.path) {
            return;
          }

          onClose();
          push(path || suggestion.path || '');
          break;
        default: {
          // Prevent race condition when pressing enter quickly after a search
          // because `query` and `suggestion.text` states are out of sync.
          const currentQuery = suggestion.navId === 0 ? query : suggestion.text;
          onClose();
          addRecentSearch(currentQuery);
          push(`/search?search=${encodeURIComponent(currentQuery)}`);
        }
      }
    },
    [history, onClose, query]
  );

  const handleClick = useCallback(
    async (suggestion: Suggestion) => {
      const { push } = history;

      if (
        (suggestion.type === 'original' || !suggestion.text) &&
        query.length < 2
      ) {
        // Don't redirect to the full page result for queries that are too short or not set. It will just fail.
        return;
      }

      // Log a suggestion click if the user clicked on a suggestion (not on the query itself)
      if (
        suggestion.type &&
        suggestion.type !== 'original' &&
        suggestion.type !== 'history'
      ) {
        dispatch(actions.suggestionClick(suggestion.type, suggestion.navId));
      }

      setCurrentSuggestion(suggestion);

      switch (suggestion.type) {
        // Try to resolve correct. If fail, fall through to default
        case 'product':
        case 'vcategory':
        case 'category':
        case 'brand_category':
        case 'product_collection':
        case 'offer_collection':
        case 'store': {
          try {
            const url = await gotoUrl(
              suggestion.type,
              suggestion.id,
              suggestion.text,
              suggestion.meta
            );
            onClose();
            push(url);
            break;
            // eslint-disable-next-line no-empty
          } catch (e) {}
        }
        // eslint-disable-next-line no-fallthrough
        default: {
          // Prevent race condition when pressing enter quickly after a search
          // because `query` and `suggestion.text` states are out of sync.
          const currentQuery =
            suggestion.type === 'original' ? query : suggestion.text;
          onClose();
          addRecentSearch(currentQuery);
          push(`/search?search=${encodeURIComponent(currentQuery)}`);
        }
      }
    },
    [history, onClose, query, setCurrentSuggestion, dispatch]
  );

  const handleKeyPress = useCallback(
    (e: KeyboardEvent) => {
      const key = e.key || e.keyCode;
      const activeSuggestions = (newPredictionsApi
        ? // Sort based on `navId` for navigation to work when changing displayed order
          newSuggestions.sort((a, b) => (a.navId > b.navId ? 1 : -1))
        : suggestions) as Array<Suggestion | NewSuggestion>;
      const handler = (newPredictionsApi ? handleClickNew : handleClick) as (
        s: Suggestion | NewSuggestion
      ) => void;

      switch (key) {
        case 'Enter':
        case 13: {
          const suggestion = activeSuggestions.find(s => s.navId === selected);

          if (suggestion) {
            handler(suggestion);
            if (searchInputElement) {
              searchInputElement.blur();
            }
            e.stopPropagation();
          } else {
            if (query.length === 0) {
              return;
            }

            handler({
              text: query,
              type: 'enter',
              navId: 0,
            });

            if (searchInputElement) searchInputElement.blur();
            e.stopPropagation();
          }
          break;
        }
        case 'ArrowRight':
        case 39: {
          const suggestion = activeSuggestions.find(
            (s: Suggestion | NewSuggestion) => s.navId === selected
          );

          if (
            suggestion &&
            searchInputElement &&
            searchInputElement.selectionStart ===
              searchInputElement.selectionEnd &&
            searchInputElement.selectionStart ===
              searchInputElement.value.length
          ) {
            searchInputElement.value = `${suggestion.text} `;

            searchInputElement.dispatchEvent(new CustomEvent('focus'));
          }
          // Make sure search bar updates text
          break;
        }
        case 'ArrowUp':
        case 'ArrowDown':
        case 38:
        case 40: {
          e.preventDefault();
          let nextItem;
          const currentIndex = activeSuggestions.findIndex(
            s => s.navId === selected
          );

          if (key === 40 || key === 'ArrowDown') {
            // ArrowDown
            nextItem =
              activeSuggestions[(currentIndex + 1) % activeSuggestions.length];
          } else {
            // ArrowUp
            const nextIndex =
              currentIndex - 1 === -1
                ? activeSuggestions.length - 1
                : currentIndex - 1;

            nextItem = activeSuggestions[nextIndex];
          }

          if (nextItem) {
            if (searchInputElement) {
              if (nextItem.type === 'SuggestedProductCollection') {
                const { text } = extractFilter(
                  nextItem as SuggestedProductCollection
                );
                searchInputElement.value = text
                  ? `${text} `
                  : `${nextItem.text} `;
              } else {
                searchInputElement.value = `${nextItem.text} `;
              }
            }

            setSelected(nextItem.navId);
          }
          break;
        }
        case 'Escape':
        case 'Esc':
        case 27:
          e.preventDefault();
          break;
        default:
          break;
      }
    },
    [
      handleClickNew,
      handleClick,
      query,
      selected,
      suggestions,
      newPredictionsApi,
      newSuggestions,
      searchInputElement,
    ]
  );

  const handleExtendSearch = (suggestion: NewSuggestion) => {
    setCurrentSuggestion(suggestion);
    if (searchInputElement) {
      searchInputElement.focus();
    }
  };

  useEffect(() => {
    if (searchInputElement) {
      searchInputElement.addEventListener('keydown', handleKeyPress);
    }
    // Remove event listeners on cleanup
    return () => {
      if (searchInputElement) {
        searchInputElement.removeEventListener('keydown', handleKeyPress);
      }
    };
  }, [handleKeyPress, searchInputElement]);

  useClickAway(containerRef, (e: Event) => {
    if (
      e.target instanceof Node &&
      (searchInputElement === e.target ||
        updatedInputElement?.current === e.target ||
        updatedSearchWrapperElement?.current?.contains(e.target) ||
        searchWrapperElement?.contains(e.target))
    ) {
      // It's the search bar, we don't want to close
      return;
    }

    onClose();
  });

  useEffect(() => {
    // Clear old suggestions when erasing input query
    if (query.length === 0) {
      if (newPredictionsApi) {
        setNewSuggestions(getRecentSearches().map(createHistorySuggestion));
      } else {
        setSuggestions(getRecentSearches().map(createHistorySuggestion));
      }
    }
  }, [query]);

  useEffect(() => {
    // Reset selected state when query changed
    if (newPredictionsApi && selected !== 0) {
      setSelected(0);
    }
  }, [query]);

  // Fetch the regular price for all products
  // Fetched in client for performance optimisations
  useDebounce(
    async () => {
      // Disable fetching of prices based on feature level
      if (window.FEATURE_TOGGLE >= 1) return;
      const productIds = newSuggestions
        .filter(s => s.__typename === 'SuggestedProduct')
        .map(p => p.__typename === 'SuggestedProduct' && p.id);

      if (productIds?.length) {
        const res = await graphql(
          `
            query productPrices($ids: [Int!]!) {
              products(ids: $ids, allowMissing: true) {
                id
                priceSummary {
                  regular
                }
              }
            }
          `,
          { ids: productIds }
        );

        if (res?.data?.products?.length) {
          setProducts(
            res.data.products.map((p: ThinProduct) => ({
              id: p.id,
              price: p.priceSummary.regular,
            }))
          );
        }
      }
    },
    100,
    [newSuggestions]
  );

  useDebounce(
    async () => {
      if (query.length === 0 || !query.trim()) {
        return;
      }

      if (newPredictionsApi) {
        const myRequestCount = requestCount.current + 1;
        requestCount.current = myRequestCount;

        try {
          const res = await graphql(suggestionsQuery, { query });

          if (myRequestCount !== requestCount.current) {
            // The response we're processing is not from the latest request, discard it
            return;
          }

          if (res.data) {
            setNewSuggestions([
              createOriginalSuggestion(query),
              ...res.data.searchSuggestions.map(
                (s: NewSuggestion, index: number) => ({
                  ...s,
                  type: s.__typename,
                  // Make sure navigation in based on the actual order displayed
                  navId:
                    (s.__typename ? SUGGESTION_ORDER[s.__typename] * 10 : 10) +
                    index,
                })
              ),
              ...getRecentSearches()
                .filter(s => filterHistory(s, query))
                .map(createHistorySuggestion),
            ]);
          }

          if (res.errors?.length || res.data.searchSuggestions[0]?.error) {
            setNewPredictions(false);
          }
        } catch (e) {
          setNewPredictions(false);
        }
        return;
      }

      const { superSearchEndpoint } = market;
      const myRequestCount = requestCount.current + 1;
      requestCount.current = myRequestCount;

      if (!superSearchEndpoint) {
        return;
      }

      // HACK: Super search endpoint has rfe hardcoded at the moment
      // Fix this when refactoring full search area
      const endpoint =
        superSearchEndpoint.replace('/rfe', '') +
        (useNewResponseFormat ? '/v2' : '');

      let res;

      try {
        res = await fetch(
          `${endpoint}/suggestions?q=${query}&market=${
            market.code
          }&searchInProperties=true${
            includePriceOnProductSuggestions ? '&includePrice=1' : ''
          }&productCollections=1`
        );
        if (res.status !== 200) {
          const searchError: SearchResultError = await res.json();
          throw new Error(`Search failed: ${searchError.error}`);
        }
      } catch (err) {
        return;
      }

      if (myRequestCount !== requestCount.current) {
        // The response we're processing is not from the latest request, discard it
        return;
      }

      let suggestionsFromApi = [];
      if (useNewResponseFormat) {
        const data: SearchResult = await res.json();

        suggestionsFromApi = getSuggestionsFromNewFormat(data);
      } else {
        const data: SuggestionsResponse = await res.json();

        if (data.error) {
          return;
        }

        suggestionsFromApi = getSuggestionsFromOldFormat(data);
      }

      const recentSearches = getRecentSearches();
      const suggestionsInState: Suggestion[] = [
        { navId: NAV_ID_ORIGINAL, text: query, type: 'original' },
        ...suggestionsFromApi,
        ...recentSearches
          .filter(s => filterHistory(s, query))
          .map(createHistorySuggestion),
      ];

      setSelected(NAV_ID_ORIGINAL);

      setSuggestions(suggestionsInState);
    },
    100,
    [query, includePriceOnProductSuggestions, useNewResponseFormat, market]
  );

  const handleDeleteHistory = (
    e: React.MouseEvent<HTMLButtonElement>,
    historyQuery: string
  ) => {
    e.stopPropagation();

    deleteRecentSearch(historyQuery);

    if (newPredictionsApi) {
      setNewSuggestions(prevState => [
        ...prevState.filter(item => item.__typename !== 'history'),
        ...getRecentSearches()
          .filter(s => filterHistory(s, query))
          .map(createHistorySuggestion),
      ]);
    } else {
      setSuggestions(prevState => [
        ...prevState.filter(item => item.type !== 'history'),
        ...getRecentSearches()
          .filter(s => filterHistory(s, query))
          .map(createHistorySuggestion),
      ]);
    }
  };

  const handleClearHistory = () => {
    clearRecentSearches();

    if (newPredictionsApi) {
      setNewSuggestions(prevState => [
        ...prevState.filter(item => item.__typename !== 'history'),
      ]);
    } else {
      setSuggestions(prevState => [
        ...prevState.filter(item => item.type !== 'history'),
      ]);
    }
  };

  if (
    !visible ||
    (!newPredictionsApi && !suggestions.length && !popularSearches.length) ||
    (newPredictionsApi && !newSuggestions.length && !popularSearches.length)
  ) {
    return null;
  }

  if (newPredictionsApi) {
    return (
      <>
        <Overlay active />
        <SuggestionBox containerRef={containerRef}>
          {popularSearches.length &&
          !newSuggestions.find(s =>
            Object.values(NewSuggestionTypeEnum).includes(
              // Check if suggestions includes any actual suggestion results
              // recast because TS won't allow this check to ever be falsy.
              s.__typename as NewSuggestionTypeEnum
            )
          ) ? (
            <>
              <S.PopularSearchesTitle>
                {t('search', 'search.popular-searches')}
              </S.PopularSearchesTitle>
              <PopularSearches
                popularSearches={popularSearches}
                onClick={onClose}
              />
            </>
          ) : null}
          <SuggestionItems
            query={query}
            items={newSuggestions}
            handleClick={handleClickNew}
            selectedId={selected}
            handleClearHistory={handleClearHistory}
            handleDeleteHistory={handleDeleteHistory}
            onExtendSearch={handleExtendSearch}
            products={products}
          />
        </SuggestionBox>
      </>
    );
  }

  return (
    <>
      <Overlay active />

      <SuggestionBox containerRef={containerRef}>
        {popularSearches.length &&
        (suggestions.every(suggestion => suggestion.type === 'history') ||
          !suggestions.length) ? (
          <>
            <S.PopularSearchesTitle>
              {t('search', 'search.popular-searches')}
            </S.PopularSearchesTitle>
            <PopularSearches
              popularSearches={popularSearches}
              onClick={onClose}
            />
          </>
        ) : null}
        {suggestions && suggestions.length >= 7 && (
          <S.AdItem style={{ order: 7 }}>
            <AdContainer
              key="dubbelpanorama1"
              className="with-position-1"
              placement="dubbelpanorama1"
              format="panorama_1"
              min={0}
              max={860}
              async
            />
          </S.AdItem>
        )}
        {suggestions &&
          suggestions.map((suggestion, i) => {
            switch (suggestion.type) {
              case 'original': {
                return (
                  <OriginalSuggestionItem
                    key="original"
                    selected={selected === NAV_ID_ORIGINAL}
                    query={query}
                    onClick={() => handleClick(suggestion)}
                  />
                );
              }
              case 'suggestion': {
                return (
                  <SuggestionItem
                    key={suggestion.text}
                    index={i}
                    text={suggestion.text}
                    selected={suggestion.navId === selected}
                    onSuggestionButtonClick={() => {
                      setCurrentSuggestion(suggestion);
                      if (searchInputElement) searchInputElement.focus();
                    }}
                    onClick={() => {
                      handleClick(suggestion);
                    }}
                  />
                );
              }
              case 'product': {
                return (
                  <ProductSuggestionItem
                    key={suggestion.text}
                    id={suggestion.id}
                    selected={suggestion.navId === selected}
                    onClick={() => handleClick(suggestion)}
                    text={suggestion.text}
                    index={i}
                    price={suggestion.price}
                    includePriceOnProductSuggestions={
                      includePriceOnProductSuggestions
                    }
                  />
                );
              }
              case 'store': {
                return (
                  <StoreSuggestionItem
                    key={suggestion.text}
                    selected={suggestion.navId === selected}
                    onClick={() => handleClick(suggestion)}
                    text={suggestion.text}
                    index={i}
                    logo={suggestion.logo}
                  />
                );
              }
              case 'history': {
                const firstHistoryItem = suggestions?.findIndex(
                  item => item.type === 'history'
                );

                return (
                  <>
                    {firstHistoryItem === i && (
                      <HistoryTitle>
                        {t('search', 'search.recent-searches')}
                      </HistoryTitle>
                    )}

                    <HistoryItem
                      key={`${suggestion.text}-${i + 100}`}
                      selected={suggestion.navId === selected}
                      onClick={() => handleClick(suggestion)}
                      onDelete={(e: React.MouseEvent<HTMLButtonElement>) =>
                        handleDeleteHistory(e, suggestion.text)
                      }
                      onClear={handleClearHistory}
                      text={suggestion.text}
                      showClear={
                        i === firstHistoryItem &&
                        suggestions.filter(s => s.type === 'history').length > 2
                      }
                    />
                  </>
                );
              }
              default:
                if (
                  suggestion.type &&
                  (suggestion.type === 'product_collection' ||
                    suggestion.type === 'offer_collection' ||
                    suggestion.type?.indexOf('category') !== -1)
                ) {
                  return (
                    <CategorySuggestionItem
                      key={suggestion.text}
                      selected={suggestion.navId === selected}
                      onClick={() => handleClick(suggestion)}
                      text={suggestion.text}
                      index={i}
                      type={suggestion.type}
                      logo={suggestion.logo}
                    />
                  );
                }
                break;
            }

            return null;
          })}
      </SuggestionBox>
    </>
  );
};

export default Suggestions;
